import React, { useState, useEffect, useRef } from 'react';
import api from '../api/client';

interface TransferTabProps {
  companies: any[];
  showAlert: (title: string, message: string) => void;
}

interface CompanyStats {
  company: { id: string; name: string };
  counts: Record<string, number>;
  storage: { used: number; limit: number; usedMB: number; limitMB: number };
}

interface HistoryEntry {
  ts: number;
  companyName: string;
  kind: 'zip' | 'folder' | 'xml';
  action: 'export' | 'import';
  summary: string;
  ok: boolean;
}

const STORAGE_KEY = 'hrlama_transfer_history_v1';
const MAX_IMPORT_SIZE_MB = 500;

const loadHistory = (): HistoryEntry[] => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
};
const saveHistory = (entries: HistoryEntry[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, 15)));
};

const checkFileSize = (file: File | null): { ok: boolean; error?: string } => {
  if (!file) return { ok: true };
  const sizeMB = file.size / 1024 / 1024;
  if (sizeMB > MAX_IMPORT_SIZE_MB) {
    return { ok: false, error: 'Файл ' + sizeMB.toFixed(0) + ' МБ больше лимита ' + MAX_IMPORT_SIZE_MB + ' МБ' };
  }
  return { ok: true };
};

const fmtSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' Б';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' КБ';
  return (bytes / 1024 / 1024).toFixed(2) + ' МБ';
};

const fmtDate = (ts: number) => {
  const d = new Date(ts);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// ═══════════════════════════════════════════════════════════
// Общие стили
// ═══════════════════════════════════════════════════════════
const S = {
  wrap: { marginTop: 20, display: 'flex', flexDirection: 'column' as const, gap: 24 },
  h1: { fontSize: 26, fontWeight: 800, margin: 0, color: '#1e293b' },
  subtitle: { fontSize: 14, color: '#64748b', margin: '4px 0 0', lineHeight: 1.5 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20 },
  card: {
    background: '#fff',
    padding: 24,
    borderRadius: 16,
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 14,
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: 14 },
  icon: (color: string) => ({
    width: 44, height: 44,
    background: color,
    borderRadius: 12,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 22,
  }),
  cardTitle: { margin: 0, fontSize: 17, fontWeight: 700, color: '#1e293b' },
  cardDesc: { margin: 0, fontSize: 13, color: '#64748b', lineHeight: 1.4 },
  select: {
    width: '100%', padding: '11px 12px', borderRadius: 8,
    border: '1px solid #cbd5e1', fontSize: 14, color: '#0f172a',
    background: '#fff', cursor: 'pointer',
  },
  fileLabel: {
    padding: '12px 16px',
    borderRadius: 8,
    border: '2px dashed #cbd5e1',
    color: '#64748b',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    transition: 'all 0.15s',
  },
  fileLabelActive: { borderColor: '#3b82f6', color: '#1e40af', background: '#eff6ff' },
  btn: (bg: string) => ({
    padding: '12px 18px',
    borderRadius: 8,
    background: bg,
    color: '#fff',
    border: 'none',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  }),
  btnSecondary: (bg: string) => ({
    ...({} as any),
    padding: '12px 18px',
    borderRadius: 8,
    background: bg,
    color: '#fff',
    border: 'none',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  }),
  btnDisabled: { opacity: 0.55, cursor: 'not-allowed' },
  btnRow: { display: 'flex', gap: 8 },
  statsRow: { display: 'flex', gap: 10, flexWrap: 'wrap' as const },
  statPill: {
    padding: '6px 10px',
    borderRadius: 999,
    background: '#f1f5f9',
    fontSize: 12,
    color: '#475569',
    fontWeight: 600,
  },
  preview: {
    marginTop: 12,
    padding: 14,
    background: '#f0f9ff',
    borderRadius: 10,
    border: '1px solid #bae6fd',
    fontSize: 13,
    color: '#0c4e7a',
    lineHeight: 1.6,
  },
  previewTitle: { fontWeight: 700, marginBottom: 6, color: '#0369a1' },
  pre: {
    fontSize: 11,
    background: '#fff',
    padding: 10,
    borderRadius: 6,
    overflow: 'auto',
    maxHeight: 220,
    margin: '6px 0 0',
    whiteSpace: 'pre-wrap' as const,
    color: '#334155',
  },
  warning: {
    padding: 10,
    background: '#fef3c7',
    border: '1px solid #fcd34d',
    borderRadius: 8,
    fontSize: 12,
    color: '#92400e',
    lineHeight: 1.5,
  },
  historyCard: {
    background: '#fff',
    padding: 20,
    borderRadius: 16,
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0',
  },
  historyRow: {
    padding: '8px 0',
    borderBottom: '1px solid #f1f5f9',
    fontSize: 13,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  progressBar: {
    width: '100%',
    height: 6,
    background: '#e2e8f0',
    borderRadius: 3,
    overflow: 'hidden',
    marginTop: 4,
  },
  progressFill: (w: number, color: string) => ({
    width: w + '%',
    height: '100%',
    background: color,
    transition: 'width 0.3s ease',
  }),
  storageRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    background: '#f8fafc',
    borderRadius: 10,
    marginTop: 4,
  },
  storageBar: {
    flex: 1,
    height: 8,
    background: '#e2e8f0',
    borderRadius: 4,
    overflow: 'hidden',
  },
  storageFill: (w: number) => ({
    width: Math.min(100, w) + '%',
    height: '100%',
    background: w > 90 ? '#ef4444' : w > 70 ? '#f59e0b' : '#10b981',
  }),
};

export default function TransferTab({ companies, showAlert }: TransferTabProps) {
  // Компании
  const [exportCompanyId, setExportCompanyId] = useState('');
  const [importCompanyId, setImportCompanyId] = useState('');
  const [folderCompanyId, setFolderCompanyId] = useState('');

  // Загрузки
  const [exporting, setExporting] = useState(false);
  const [zipImporting, setZipImporting] = useState(false);
  const [xmlImporting, setXmlImporting] = useState(false);
  const [folderImporting, setFolderImporting] = useState(false);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [folderDryRunLoading, setFolderDryRunLoading] = useState(false);

  // Файлы
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [xmlFile, setXmlFile] = useState<File | null>(null);
  const [folderFile, setFolderFile] = useState<File | null>(null);

  // Опции
  const [forceZip, setForceZip] = useState(false);
  const [forceXml, setForceXml] = useState(false);
  const [forceFolder, setForceFolder] = useState(false);

  // Предпросмотры
  const [xmlPreview, setXmlPreview] = useState<any>(null);
  const [folderPreview, setFolderPreview] = useState<any>(null);

  // Статистика
  const [stats, setStats] = useState<CompanyStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // История
  const [history, setHistory] = useState<HistoryEntry[]>(loadHistory());

  const pushHistory = (entry: HistoryEntry) => {
    const next = [entry, ...history].slice(0, 15);
    setHistory(next);
    saveHistory(next);
  };

  // Загружаем статистику при выборе компании
  useEffect(() => {
    const cid = exportCompanyId || importCompanyId || folderCompanyId;
    if (!cid) { setStats(null); return; }
    setStatsLoading(true);
    api.get('/transfer/stats', { params: { companyId: cid } })
      .then(r => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, [exportCompanyId, importCompanyId, folderCompanyId]);

  // ═══ Экспорт ZIP ═══
  const handleExport = async () => {
    if (!exportCompanyId) return showAlert('Ошибка', 'Выберите компанию');
    setExporting(true);
    try {
      const res = await api.get('/transfer/export/zip', {
        params: { companyId: exportCompanyId },
        responseType: 'blob',
      });
      const company = companies.find(c => (c.id || c._id) === exportCompanyId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_${company?.name || 'company'}_${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      pushHistory({
        ts: Date.now(),
        companyName: company?.name || '—',
        kind: 'zip', action: 'export', ok: true,
        summary: `Экспорт ZIP (${fmtSize(res.data.size)})`,
      });
      showAlert('Успех', 'Архив скачан');
    } catch (e: any) {
      const errMsg = e.response?.status === 413
        ? 'Архив больше лимита ' + MAX_IMPORT_SIZE_MB + ' МБ. Разделите на части.'
        : (e.response?.data?.error || 'Не удалось скачать архив');
      pushHistory({ ts: Date.now(), companyName: '—', kind: 'zip', action: 'export', ok: false, summary: errMsg });
      showAlert('Ошибка', errMsg);
    } finally { setExporting(false); }
  };

  // ═══ Импорт ZIP (наш формат) ═══
  const handleZipImport = async () => {
    if (!importCompanyId) return showAlert('Ошибка', 'Выберите компанию');
    if (!zipFile) return showAlert('Ошибка', 'Выберите ZIP-файл');
    setZipImporting(true);
    const fd = new FormData();
    fd.append('file', zipFile);
    fd.append('targetCompanyId', importCompanyId);
    if (forceZip) fd.append('force', 'true');
    try {
      const res = await api.post('/transfer/import/zip', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data?.details || {};
      const total = Object.values(d.records || d).reduce((s: number, v: any) => s + (typeof v === 'number' ? v : 0), 0);
      const company = companies.find(c => (c.id || c._id) === importCompanyId);
      pushHistory({
        ts: Date.now(), companyName: company?.name || '—', kind: 'zip', action: 'import', ok: true,
        summary: `ZIP: ${total} записей, ${d.files || 0} файлов`,
      });
      showAlert('Успех', `Импорт завершён. Записей: ${total}, файлов: ${d.files || 0}`);
      setZipFile(null);
    } catch (e: any) {
      const err = e.response?.data?.error || 'Ошибка импорта ZIP';
      pushHistory({ ts: Date.now(), companyName: '—', kind: 'zip', action: 'import', ok: false, summary: err });
      showAlert('Ошибка', err);
    } finally { setZipImporting(false); }
  };

  // ═══ Импорт XML (универсальный) ═══
  const handleXmlDryRun = async () => {
    if (!xmlFile || !importCompanyId) return showAlert('Ошибка', 'Выберите файл и компанию');
    setDryRunLoading(true); setXmlPreview(null);
    const fd = new FormData();
    fd.append('file', xmlFile);
    fd.append('targetCompanyId', importCompanyId);
    try {
      const res = await api.post('/transfer/import/xml?dryRun=true', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setXmlPreview(res.data);
    } catch (e: any) {
      showAlert('Ошибка', e.response?.data?.error || 'Не удалось проанализировать');
    } finally { setDryRunLoading(false); }
  };

  const handleXmlImport = async () => {
    if (!xmlFile || !importCompanyId) return showAlert('Ошибка', 'Выберите файл и компанию');
    setXmlImporting(true);
    const fd = new FormData();
    fd.append('file', xmlFile);
    fd.append('targetCompanyId', importCompanyId);
    if (forceXml) fd.append('force', 'true');
    try {
      const res = await api.post('/transfer/import/xml', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data?.details || {};
      const total = Object.values(d).reduce((s: number, v: any) => s + (v.created || 0), 0);
      const company = companies.find(c => (c.id || c._id) === importCompanyId);
      pushHistory({
        ts: Date.now(), companyName: company?.name || '—', kind: 'xml', action: 'import', ok: true,
        summary: `XML: ${total} записей`,
      });
      showAlert('Успех', `Импортировано записей: ${total}`);
      setXmlFile(null); setXmlPreview(null);
    } catch (e: any) {
      const err = e.response?.data?.error || 'Ошибка импорта XML';
      pushHistory({ ts: Date.now(), companyName: '—', kind: 'xml', action: 'import', ok: false, summary: err });
      showAlert('Ошибка', err);
    } finally { setXmlImporting(false); }
  };

  // ═══ Импорт папки ═══
  const handleFolderDryRun = async () => {
    if (!folderFile || !folderCompanyId) return showAlert('Ошибка', 'Выберите папку и компанию');
    setFolderDryRunLoading(true); setFolderPreview(null);
    const fd = new FormData();
    fd.append('file', folderFile);
    fd.append('targetCompanyId', folderCompanyId);
    try {
      const res = await api.post('/transfer/import/folder?dryRun=true', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFolderPreview(res.data);
    } catch (e: any) {
      showAlert('Ошибка', e.response?.data?.error || 'Не удалось проанализировать');
    } finally { setFolderDryRunLoading(false); }
  };

  const handleFolderImport = async () => {
    if (!folderFile || !folderCompanyId) return showAlert('Ошибка', 'Выберите папку и компанию');
    setFolderImporting(true);
    const fd = new FormData();
    if (forceFolder) fd.append('force', 'true');
    fd.append('targetCompanyId', folderCompanyId);
    fd.append('file', folderFile);
    try {
      const url = forceFolder ? '/transfer/import/folder?force=true' : '/transfer/import/folder';
      const res = await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data?.data || {};
      const total = Object.values(d).reduce((s: number, v: any) => s + (v.created || 0), 0);
      const files = res.data?.files || {};
      const company = companies.find(c => (c.id || c._id) === folderCompanyId);
      pushHistory({
        ts: Date.now(), companyName: company?.name || '—', kind: 'folder', action: 'import', ok: true,
        summary: `Папка: ${total} записей, ${files.saved || 0} файлов (связано ${files.linked || 0})`,
      });
      showAlert('Успех', `Импорт папки завершён.\nЗаписей: ${total}\nФайлов: ${files.saved || 0}, связано: ${files.linked || 0}`);
      setFolderFile(null); setFolderPreview(null);
    } catch (e: any) {
      const err = e.response?.data?.error || 'Ошибка импорта папки';
      pushHistory({ ts: Date.now(), companyName: '—', kind: 'folder', action: 'import', ok: false, summary: err });
      showAlert('Ошибка', err);
    } finally { setFolderImporting(false); }
  };

  return (
    <div style={S.wrap}>
      <div>
        <h1 style={S.h1}>📦 Управление данными</h1>
        <p style={S.subtitle}>
          Экспорт и импорт HRM-данных компании. Все операции изолированы: данные одной компании
          недоступны другой. Экспорт включает сотрудников, кандидатов, вакансии, клиентов, документы и связанные файлы.
        </p>
      </div>

      {/* ═══ Статистика компании ═══ */}
      {stats && (
        <div style={{ ...S.card, padding: 18, flexDirection: 'row', flexWrap: 'wrap', gap: 20, alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Компания</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>{stats.company.name}</div>
          </div>
          <div style={S.statsRow}>
            <span style={S.statPill}>👤 Сотрудники: {stats.counts.employees}</span>
            <span style={S.statPill}>🎯 Кандидаты: {stats.counts.candidates}</span>
            <span style={S.statPill}>💼 Вакансии: {stats.counts.vacancies}</span>
            <span style={S.statPill}>🏢 Клиенты: {stats.counts.clients}</span>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b' }}>
              <span>💾 Хранилище</span>
              <span>{stats.storage.usedMB} / {stats.storage.limitMB} МБ</span>
            </div>
            <div style={S.storageBar}>
              <div style={S.storageFill(stats.storage.limit > 0 ? (stats.storage.used / stats.storage.limit) * 100 : 0)} />
            </div>
          </div>
        </div>
      )}

      <div style={S.grid}>
        {/* ═══ КАРТОЧКА 1: Экспорт ═══ */}
        <div style={S.card}>
          <div style={S.cardHeader}>
            <div style={S.icon('#dbeafe')}>📤</div>
            <div>
              <h3 style={S.cardTitle}>Экспорт компании</h3>
              <p style={S.cardDesc}>Скачать ZIP с HRM-данными и файлами</p>
            </div>
          </div>

          <select value={exportCompanyId} onChange={e => setExportCompanyId(e.target.value)} style={S.select}>
            <option value="">— Выберите компанию —</option>
            {companies.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>)}
          </select>

          <div style={{ fontSize: 12, color: '#64748b' }}>
            В архив войдут: сотрудники, кандидаты, вакансии, клиенты, документы, кадровый резерв, requisition,
            а также файлы (резюме, фото, сканы), привязанные к записям компании.
          </div>

          <button
            onClick={handleExport}
            disabled={!exportCompanyId || exporting}
            style={{ ...S.btn('#2563eb'), ...((!exportCompanyId || exporting) ? S.btnDisabled : {}) }}
          >
            {exporting ? '⏳ Готовим архив...' : '📦 Скачать ZIP'}
          </button>
        </div>

        {/* ═══ КАРТОЧКА 2: Импорт ZIP ═══ */}
        <div style={S.card}>
          <div style={S.cardHeader}>
            <div style={S.icon('#dcfce7')}>📥</div>
            <div>
              <h3 style={S.cardTitle}>Импорт ZIP (наш формат)</h3>
              <p style={S.cardDesc}>Восстановить данные из архива, сделанного в HR LAMA</p>
            </div>
          </div>

          <select value={importCompanyId} onChange={e => setImportCompanyId(e.target.value)} style={S.select}>
            <option value="">— Куда импортировать? —</option>
            {companies.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>)}
          </select>

          <input type="file" accept=".zip" id="zip-input" style={{ display: 'none' }}
            onChange={e => {
              const f = e.target.files?.[0] || null;
              const chk = checkFileSize(f);
              if (!chk.ok) { showAlert('Файл слишком большой', chk.error || ''); setZipFile(null); e.target.value = ''; return; }
              setZipFile(f);
            }} />
          <label htmlFor="zip-input" style={{ ...S.fileLabel, ...(zipFile ? S.fileLabelActive : {}) }}>
            {zipFile ? '✅ ' + zipFile.name + ' (' + fmtSize(zipFile.size) + ')' : '📁 Выбрать ZIP-архив (до ' + MAX_IMPORT_SIZE_MB + ' МБ)'}
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569', cursor: 'pointer' }}>
            <input type="checkbox" checked={forceZip} onChange={e => setForceZip(e.target.checked)} />
            Очистить существующие данные перед импортом
          </label>
          {forceZip && <div style={S.warning}>⚠️ Внимание: будут удалены все текущие записи HRM в выбранной компании (сотрудники, кандидаты, вакансии, клиенты, документы). Действие необратимо.</div>}

          <button
            onClick={handleZipImport}
            disabled={!importCompanyId || !zipFile || zipImporting}
            style={{ ...S.btn('#10b981'), ...((!importCompanyId || !zipFile || zipImporting) ? S.btnDisabled : {}) }}
          >
            {zipImporting ? '⏳ Импорт...' : '📥 Импортировать ZIP'}
          </button>
        </div>

        {/* ═══ КАРТОЧКА 3: Импорт папки ═══ */}
        <div style={S.card}>
          <div style={S.cardHeader}>
            <div style={S.icon('#fef3c7')}>🗂️</div>
            <div>
              <h3 style={S.cardTitle}>Импорт папки из другой системы</h3>
              <p style={S.cardDesc}>ZIP с XML/CSV/JSON и файлами — данные + вложения</p>
            </div>
          </div>

          <select value={folderCompanyId} onChange={e => setFolderCompanyId(e.target.value)} style={S.select}>
            <option value="">— Куда импортировать? —</option>
            {companies.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>)}
          </select>

          <input type="file" accept=".zip" id="folder-input" style={{ display: 'none' }}
            onChange={e => {
              const f = e.target.files?.[0] || null;
              const chk = checkFileSize(f);
              if (!chk.ok) { showAlert('Файл слишком большой', chk.error || ''); setFolderFile(null); e.target.value = ''; return; }
              setFolderFile(f);
              setFolderPreview(null);
            }} />
          <label htmlFor="folder-input" style={{ ...S.fileLabel, ...(folderFile ? S.fileLabelActive : {}) }}>
            {folderFile ? '✅ ' + folderFile.name + ' (' + fmtSize(folderFile.size) + ')' : '📁 Выбрать ZIP-папку (до ' + MAX_IMPORT_SIZE_MB + ' МБ)'}
          </label>

          <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
            Поддерживается: 1С, hh.ru, Huntflow, BambooHR, SAP и любые HRM. Внутри ZIP могут быть
            <b> XML/CSV/JSON</b> с данными и <b>файлы</b> (pdf, doc, jpg, png) — они привяжутся к сотрудникам/вакансиям
            по имени файла (с транслитерацией).
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569', cursor: 'pointer' }}>
            <input type="checkbox" checked={forceFolder} onChange={e => setForceFolder(e.target.checked)} />
            Очистить существующие данные перед импортом
          </label>
          {forceFolder && <div style={S.warning}>⚠️ Внимание: будут удалены все текущие записи HRM в выбранной компании. Действие необратимо.</div>}

          <div style={S.btnRow}>
            <button
              onClick={handleFolderDryRun}
              disabled={!folderCompanyId || !folderFile || folderDryRunLoading}
              style={{ flex: 1, ...S.btn('#f59e0b'), ...((!folderCompanyId || !folderFile || folderDryRunLoading) ? S.btnDisabled : {}) }}
            >
              {folderDryRunLoading ? '⏳ Анализ...' : '🔍 Предпросмотр'}
            </button>
            <button
              onClick={handleFolderImport}
              disabled={!folderCompanyId || !folderFile || folderImporting}
              style={{ flex: 1, ...S.btn('#10b981'), ...((!folderCompanyId || !folderFile || folderImporting) ? S.btnDisabled : {}) }}
            >
              {folderImporting ? '⏳ Импорт...' : '📥 Импортировать'}
            </button>
          </div>

          {/* ДЕТАЛЬНЫЙ ПРЕДПРОСМОТР ДАННЫХ */}
          {folderPreview && folderPreview.dataFiles && folderPreview.dataFiles.length > 0 && (
            <div style={{ marginTop: 16, padding: 16, background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
                📋 Детализация по файлам
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {folderPreview.dataFiles.map((file: any, i: number) => (
                  <div key={i} style={{ background: '#fff', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, wordBreak: 'break-all' }}>
                      📄 {file.name || file.file || 'Файл'}
                    </div>
                    {file.error ? (
                      <div style={{ color: '#ef4444', fontSize: 12 }}>❌ Ошибка: {file.error}</div>
                    ) : file.parsed ? (
                      <div style={{ fontSize: 12, color: '#334155' }}>
                        <div>Тип: <b>{file.type || 'Неизвестно'}</b></div>
                        <div>Полей распознано: <b>{file.fieldsCount || 0}</b></div>
                        <div style={{ marginTop: 4, color: '#64748b', fontStyle: 'italic' }}>
                          {file.type === 'candidate' && 'Данные попадут в раздел «Кандидаты»'}
                          {file.type === 'vacancy' && 'Данные попадут в раздел «Вакансии»'}
                          {file.type === 'client' && 'Данные попадут в раздел «Клиенты»'}
                          {file.type === 'employee' && 'Данные попадут в раздел «Сотрудники»'}
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: '#f59e0b', fontSize: 12 }}>⚠️ Данные не распознаны</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {folderPreview && (
            <div style={S.preview}>
              <div style={S.previewTitle}>🔍 Найдено в папке</div>
              <div>📄 Файлов данных: <b>{folderPreview.dataFiles?.length || 0}</b></div>
              <div>📎 Файлов-вложений: <b>{folderPreview.assetFilesCount || 0}</b> ({fmtSize(folderPreview.assetFilesSize || 0)})</div>
              <div style={{ marginTop: 6 }}>
                👤 Сотрудники: <b>{folderPreview.detected?.employees || 0}</b> ·{' '}
                🎯 Кандидаты: <b>{folderPreview.detected?.candidates || 0}</b> ·{' '}
                💼 Вакансии: <b>{folderPreview.detected?.vacancies || 0}</b> ·{' '}
                🏢 Клиенты: <b>{folderPreview.detected?.clients || 0}</b>
              </div>
              {folderPreview.fileMatches?.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Показать соответствие файлов ({folderPreview.fileMatches.length})</summary>
                  <pre style={S.pre}>{JSON.stringify(folderPreview.fileMatches, null, 2)}</pre>
                </details>
              )}
              {folderPreview.parseErrors?.length > 0 && (
                <div style={{ marginTop: 8, color: '#b91c1c' }}>
                  ⚠️ Ошибок парсинга: {folderPreview.parseErrors.length}
                  <pre style={S.pre}>{JSON.stringify(folderPreview.parseErrors, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ═══ ИСТОРИЯ ═══ */}
      {history.length > 0 && (
        <div style={S.historyCard}>
          <h3 style={{ ...S.cardTitle, marginBottom: 12 }}>📜 История операций</h3>
          {history.map((h, i) => (
            <div key={i} style={S.historyRow}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <span style={{ fontSize: 16 }}>{h.ok ? '✅' : '❌'}</span>
                <span style={{ color: '#64748b', fontSize: 12, minWidth: 110 }}>{fmtDate(h.ts)}</span>
                <span style={{ fontWeight: 600 }}>{h.companyName}</span>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>
                  {h.kind === 'zip' ? 'ZIP' : h.kind === 'folder' ? 'Папка' : 'XML'} {h.action === 'export' ? '→' : '←'}
                </span>
                <span style={{ color: '#475569', fontSize: 12 }}>{h.summary}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
